import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import axios from 'axios';
import AuthLayout from '../../components/auth/AuthLayout';
import AuthButton from '../../components/auth/AuthButton';
import { resolveRedirectRoute } from '../../lib/auth/redirect-routes';

type LoginProfile = 'operator' | 'external' | null;

interface AuthConfig {
  company_logo_path: string;
  support_email: string;
  auth: {
    credentials_enabled: boolean;
    iam_enabled: boolean;
    iam_provider: string | null;
    spid_enabled: boolean;
    external_login_enabled: boolean;
    operator_credentials_enabled: boolean;
    external_credentials_enabled: boolean;
    operator_spid_enabled: boolean;
    external_spid_enabled: boolean;
  };
}

export default function LoginPage() {
  const router = useRouter();
  const [config, setConfig] = useState<AuthConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState<LoginProfile>(null);

  const resolveRedirect = (target?: string): string =>
    resolveRedirectRoute(target, '/dashboard/');

  useEffect(() => {
    // Verifica se l'utente ha una sessione attiva (cookie di sessione)
    axios
      .get('/api/auth/redirect-target')
      .then((res) => {
        if (res.data?.authenticated) {
          window.location.href = resolveRedirect(res.data?.redirect_target);
        } else {
          loadAuthConfig();
        }
      })
      .catch(() => {
        loadAuthConfig();
      });
  }, [router]);

  const loadAuthConfig = () => {
    axios
      .get('/api/auth/config')
      .then((res) => {
        setConfig(res.data);
        const c = res.data.auth;

        const operatorSpid = c.spid_enabled && c.operator_spid_enabled;
        if (!c.external_login_enabled && !c.iam_enabled && !operatorSpid && c.operator_credentials_enabled) {
          router.replace('/auth/credentials/');
          return;
        }

        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  // Listen for IAM login success from popup (BroadcastChannel + postMessage fallback)
  useEffect(() => {
    const onLoginSuccess = (data: { redirect_target?: string }) => {
      // Regenerate access_token cookie for this window, then redirect
      axios
        .post('/api/auth/token')
        .then((res) => {
          window.location.href = resolveRedirect(res.data?.redirect_target ?? data?.redirect_target);
        })
        .catch(() => {
          window.location.href = resolveRedirect(data?.redirect_target);
        });
    };

    // Primary: BroadcastChannel (works when window.opener is null due to COOP headers)
    let bc: BroadcastChannel | null = null;
    try {
      bc = new BroadcastChannel('iam-login');
      bc.onmessage = (event) => {
        if (event.data?.type === 'iam-login-success') {
          onLoginSuccess(event.data);
        }
      };
    } catch (e) {}

    // Secondary: window.postMessage
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type === 'iam-login-success') {
        onLoginSuccess(event.data);
      }
    };
    window.addEventListener('message', handleMessage);

    return () => {
      bc?.close();
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  const handleIamLogin = () => {
    if (!config?.auth.iam_provider) return;
    const width = 500;
    const height = 600;
    const left = Math.round(window.screenX + (window.outerWidth - width) / 2);
    const top = Math.round(window.screenY + (window.outerHeight - height) / 2);
    window.open(
      `/login/iam/${config.auth.iam_provider}?popup=1`,
      'iam-login',
      `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes,resizable=yes,status=no,location=no`,
    );
  };

  if (loading) {
    return (
      <AuthLayout title="Accedi al Sistema">
        <div className="tw-py-10">
          <div className="tw-flex tw-justify-center">
            <div className="tw-w-8 tw-h-8 tw-border-[3px] tw-border-gray-200 tw-border-t-blue-500 tw-rounded-full tw-animate-spin" />
          </div>
        </div>
      </AuthLayout>
    );
  }

  const showExternalOption = config?.auth.external_login_enabled;
  const showOperatorOption = !!(config?.auth.iam_enabled || config?.auth.operator_spid_enabled || config?.auth.operator_credentials_enabled);
  const showProfileChoice = showOperatorOption && showExternalOption;

  const operatorAccessMethods = [
    config?.auth.iam_enabled && 'Credenziali IAM',
    config?.auth.operator_spid_enabled && 'SPID',
    config?.auth.operator_credentials_enabled && 'Email aziendale e password',
  ].filter(Boolean).join(', ');

  // Profile selection screen
  if (showProfileChoice && profile === null) {
    return (
      <AuthLayout title="Accedi al Sistema">
        <p className="tw-text-sm tw-text-gray-500 tw-mb-6">Seleziona il tuo profilo per continuare</p>

        <div className="tw-flex tw-flex-col tw-gap-3 tw-mb-4">
          {/* Operator profile */}
          <button
            type="button"
            className="tw-flex tw-items-center tw-gap-4 tw-p-4 tw-rounded-xl tw-border-2 tw-border-gray-200 tw-bg-white tw-text-left tw-cursor-pointer tw-transition-all tw-duration-200 hover:tw-border-blue-500 hover:tw-shadow-md hover:-tw-translate-y-0.5"
            onClick={() => setProfile('operator')}
          >
            <div className="tw-w-12 tw-h-12 tw-rounded-full tw-bg-blue-50 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
            </div>
            <div>
              <div className="tw-font-semibold tw-text-gray-800 tw-text-[15px]">Personale ASL</div>
              {operatorAccessMethods && (
                <div className="tw-text-xs tw-text-gray-400 tw-mt-0.5">{operatorAccessMethods}</div>
              )}
            </div>
            <svg className="tw-ml-auto tw-text-gray-300 tw-flex-shrink-0" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>

          {/* External user profile */}
          {showExternalOption && (
            <button
              type="button"
              className="tw-flex tw-items-center tw-gap-4 tw-p-4 tw-rounded-xl tw-border-2 tw-border-gray-200 tw-bg-white tw-text-left tw-cursor-pointer tw-transition-all tw-duration-200 hover:tw-border-green-500 hover:tw-shadow-md hover:-tw-translate-y-0.5"
              onClick={() => setProfile('external')}
            >
              <div className="tw-w-12 tw-h-12 tw-rounded-full tw-bg-green-50 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
              </div>
              <div>
                <div className="tw-font-semibold tw-text-gray-800 tw-text-[15px]">Utente Esterno</div>
                <div className="tw-text-xs tw-text-gray-400 tw-mt-0.5">Assistiti, sostituti, collaboratori esterni</div>
              </div>
              <svg className="tw-ml-auto tw-text-gray-300 tw-flex-shrink-0" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
          )}
        </div>
      </AuthLayout>
    );
  }

  // Auth methods screen (after profile is selected or if no profile choice needed)
  const isOperator = profile === 'operator' || (!showProfileChoice && showOperatorOption);
  const showIam = isOperator && config?.auth.iam_enabled;
  const showSpid = config?.auth.spid_enabled && (isOperator
    ? config?.auth.operator_spid_enabled
    : config?.auth.external_spid_enabled);
  const showCredentials = isOperator
    ? config?.auth.operator_credentials_enabled
    : config?.auth.external_credentials_enabled;

  // If only credentials for this profile, go directly to credentials page
  const methodCount = [showIam, showSpid, showCredentials].filter(Boolean).length;
  if (methodCount <= 1 && showCredentials) {
    router.replace('/auth/credentials/');
    return (
      <AuthLayout title="Accedi al Sistema">
        <div className="tw-py-10">
          <div className="tw-flex tw-justify-center">
            <div className="tw-w-8 tw-h-8 tw-border-[3px] tw-border-gray-200 tw-border-t-blue-500 tw-rounded-full tw-animate-spin" />
          </div>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Accedi al Sistema"
      onBack={showProfileChoice ? () => setProfile(null) : undefined}
      backLabel="Cambia profilo"
    >
      <p className="tw-text-xs tw-text-gray-400 tw-mb-3 tw-uppercase tw-tracking-wide tw-font-medium">
        {isOperator ? 'Accesso Personale ASL' : 'Accesso Utente Esterno'}
      </p>

      <div className="tw-flex tw-flex-col tw-gap-3 tw-mb-6">
        {showIam && (
          <AuthButton variant="iam" onClick={handleIamLogin}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
            Accedi con credenziali IAM
          </AuthButton>
        )}

        {showSpid && (
          <AuthButton variant="spid">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            </svg>
            Accedi con SPID
          </AuthButton>
        )}

        {showCredentials && (
          <>
            {(showIam || showSpid) && (
              <div className="tw-flex tw-items-center tw-my-1">
                <hr className="tw-flex-grow tw-border-none tw-border-t tw-border-gray-200" />
                <span className="tw-mx-3 tw-text-xs tw-text-gray-400">oppure</span>
                <hr className="tw-flex-grow tw-border-none tw-border-t tw-border-gray-200" />
              </div>
            )}
            <AuthButton variant="generate" onClick={() => router.push('/auth/credentials/')}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="4" width="20" height="16" rx="2" />
                <path d="M22 7l-10 7L2 7" />
              </svg>
              Accedi con email e password
            </AuthButton>
          </>
        )}
      </div>
    </AuthLayout>
  );
}
